import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import {
  IN_CANVAS_MOUSE_EDGES_SPEED,
  MOUSE_EDGES_DISTANCE,
  MOUSE_EDGES_SPEED,
} from '@/app/gridGL/interaction/pointer/pointerUtils';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { pixiApp, type PixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { Decelerate } from '@/app/gridGL/pixiApp/viewport/Decelerate';
import { Drag } from '@/app/gridGL/pixiApp/viewport/Drag';
import { tryApplyEmbedInitialViewOnce } from '@/app/gridGL/pixiApp/viewport/embedInitialView';
import { MouseEdges } from '@/app/gridGL/pixiApp/viewport/MouseEdges';
import { BORDER_VISIBILITY_OFFSET, getViewportClampConfig } from '@/app/gridGL/pixiApp/viewport/viewportClamp';
import { HORIZONTAL_SCROLL_KEY, Wheel, ZOOM_KEY } from '@/app/gridGL/pixiApp/viewport/Wheel';
import { embedClampToContent, embedClampToRange, embedDisableZoom, embedInitialViewRange } from '@/app/helpers/isEmbed';
import { renderWebWorker } from '@/app/web-workers/renderWebWorker/renderWebWorker';
import { Viewport as PixiViewport, type IMouseEdgesOptions } from 'pixi-viewport';
import { Point, Rectangle } from 'pixi.js';
import { isMobile } from 'react-device-detect';

const MULTIPLAYER_VIEWPORT_EASE_TIME = 100;
const MINIMUM_VIEWPORT_SCALE = 0.01;
const MAXIMUM_VIEWPORT_SCALE = 10;
const WHEEL_ZOOM_PERCENT = 1.5;

const WAIT_TO_SNAP_TIME = 200;
const SNAPPING_TIME = 50;

type SnapState = 'waiting' | 'snapping' | undefined;

export class Viewport extends PixiViewport {
  private pixiApp: PixiApp;

  private lastViewportPosition: Point = new Point();

  // setting this to 0 ensures that on initial render, the viewport is properly scaled and updated
  private lastViewportScale = 0;

  private lastScreenWidth = 0;
  private lastScreenHeight = 0;

  private lastSheetId = '';

  private waitForZoomEnd = false;

  private snapState?: SnapState;
  private snapTimeout?: number;

  constructor(pixiApp: PixiApp) {
    super({
      events: pixiApp.renderer.events,
    });
    this.pixiApp = pixiApp;
    this.plugins.add(
      'drag',
      new Drag(this, {
        pressDrag: true,
        wheel: false, // handled by Wheel plugin below
        keyToPress: isMobile ? undefined : ['Space'],
      })
    );
    this.plugins.add('decelerate', new Decelerate(this));
    if (!embedDisableZoom) {
      this.pinch().clampZoom({
        minScale: MINIMUM_VIEWPORT_SCALE,
        maxScale: MAXIMUM_VIEWPORT_SCALE,
      });
    }
    this.plugins.add(
      'wheel',
      new Wheel(this, {
        trackpadPinch: !embedDisableZoom,
        wheelZoom: !embedDisableZoom,
        percent: WHEEL_ZOOM_PERCENT,
        keyToPress: [...ZOOM_KEY, ...HORIZONTAL_SCROLL_KEY],
      })
    );
    if (!isMobile) {
      this.plugins.add(
        'drag-middle-mouse',
        new Drag(this, {
          pressToDrag: true,
          mouseButtons: 'middle',
          wheel: 'false',
        })
      );
    }

    // hack to ensure pointermove works outside of canvas
    this.off('pointerout');

    this.on('moved', this.viewportChanged);
    this.on('moved', this.handleMoved);
    this.on('zoomed', this.viewportChanged);
    this.on('wait-for-zoom-end', this.handleWaitForZoomEnd);
    this.on('zoom-end', this.handleZoomEnd);
    this.on('pinch-start', this.handleWaitForZoomEnd);
    this.on('pinch-end', this.handleZoomEnd);
    this.on('snap-end', this.handleSnapEnd);
    this.on('mouse-edge-move', this.handleMouseEdgeMove);
  }

  private viewportChanged = () => {
    events.emit('viewportChanged');
  };

  destroy() {
    this.off('moved', this.viewportChanged);
    this.off('moved', this.handleMoved);
    this.off('zoomed', this.viewportChanged);
    this.off('wait-for-zoom-end', this.handleWaitForZoomEnd);
    this.off('zoom-end', this.handleZoomEnd);
    this.off('pinch-start', this.handleWaitForZoomEnd);
    this.off('pinch-end', this.handleZoomEnd);
    this.off('snap-end', this.handleSnapEnd);
    this.off('mouse-edge-move', this.handleMouseEdgeMove);

    // Run each plugin's destroy() lifecycle so they remove the document/window
    // listeners they registered (e.g. Wheel's Safari gesture handlers).
    this.plugins.removeAll();
  }

  loadViewport() {
    const persisted = sheets.sheet.cursor.persistedViewport;
    if (persisted) {
      this.position.set(persisted.x, persisted.y);
      this.scale.set(persisted.scaleX, persisted.scaleY);
      this.dirty = true;
    } else {
      this.reset();
    }
    this.applyInitialClampPosition();
    if (embedInitialViewRange) {
      const appliedEmbedInitialView = tryApplyEmbedInitialViewOnce();
      if (appliedEmbedInitialView) {
        // headingSize.width/height are derived from scale; tryApplyEmbedInitialViewOnce just changed scale
        // while headings still reflect the previous zoom. Clamp uses those values for leftClamp/topClamp,
        // so moveCorner would target the wrong world corner until headings update.
        content.headings.dirty = true;
        content.headings.update(true);
        const clamp = this.computeContentClampSnapTargetWorldCorner();
        if (clamp.needsSnap) {
          this.moveCorner(clamp.x, clamp.y);
          this.snapState = undefined;
          this.snapTimeout = undefined;
        }
      }
    }
  }

  applyInitialClampPosition() {
    if (!embedClampToRange) return;

    // Ensure headings are up-to-date so headingSize reflects current scale/visibility.
    // Without this, headingSize may still be {0,0} on first load, causing the clamp
    // position to be off by one row/column.
    content.headings.dirty = true;
    content.headings.update(true);

    const { hasValidBounds, leftClamp, topClamp } = getViewportClampConfig(content.headings.headingSize);
    if (!hasValidBounds) return;

    this.position.set(leftClamp, topClamp);
    this.dirty = true;

    // Also clamp the cursor position to the embed bounds
    if (embedClampToContent) {
      const cursor = sheets.sheet.cursor;
      const cursorPos = cursor.position;
      const clamped = cursor.clampToEmbedBounds(cursorPos.x, cursorPos.y);
      if (clamped.x !== cursorPos.x || clamped.y !== cursorPos.y) {
        cursor.moveTo(clamped.x, clamped.y, { ensureVisible: false });
      }
    }
  }

  loadMultiplayerViewport(options: { x: number; y: number; bounds: Rectangle; sheetId: string }): void {
    const { x, y, bounds } = options;
    let width: number | undefined;
    let height: number | undefined;

    // ensure the entire follow-ee's bounds is visible to the current user
    if (this.screenWidth / this.screenHeight > bounds.width / bounds.height) {
      height = bounds.height;
    } else {
      width = bounds.width;
    }
    if (sheets.current !== options.sheetId) {
      sheets.current = options.sheetId;
      this.moveCenter(new Point(x, y));
    } else {
      this.animate({
        position: new Point(x, y),
        width,
        height,
        removeOnInterrupt: true,
        time: MULTIPLAYER_VIEWPORT_EASE_TIME,
      });
    }
    this.dirty = true;
  }

  // resets the viewport to start (respects clamp bounds if set)
  reset = () => {
    const headings = content.headings.headingSize;
    this.position.set(headings.width, headings.height);
    this.scale.set(1, 1);
    this.dirty = true;
    this.lastViewportPosition.set(0, 0);
    this.lastViewportScale = 0;
    this.lastSheetId = '';
    this.applyInitialClampPosition();
  };

  getWorld = (): Point => {
    return this.toWorld(this.pixiApp.renderer.events.pointer.global);
  };

  enableMouseEdges = (world?: Point, direction?: 'horizontal' | 'vertical') => {
    const mouseEdges = this.plugins.get('mouse-edges');
    if (mouseEdges && !mouseEdges.paused) return;
    const options: IMouseEdgesOptions = {
      allowButtons: true,
      speed: IN_CANVAS_MOUSE_EDGES_SPEED / this.scale.x,
      top: direction === 'horizontal' ? null : MOUSE_EDGES_DISTANCE,
      bottom: direction === 'horizontal' ? null : MOUSE_EDGES_DISTANCE,
      left: direction === 'vertical' ? null : MOUSE_EDGES_DISTANCE,
      right: direction === 'vertical' ? null : MOUSE_EDGES_DISTANCE,
    };
    this.plugins.add('mouse-edges', new MouseEdges(this, options));
  };

  disableMouseEdges = () => {
    this.plugins.remove('mouse-edges');
  };

  // Drives one frame of edge-scroll for a cursor position that is outside
  // the canvas (over the toolbar / sheet bar / sidebar / off the browser
  // window entirely). Called from a requestAnimationFrame loop in `Pointer`
  // while a drag is in progress and the cursor has wandered off the canvas.
  // The pixi-viewport MouseEdges plugin can't be used for this because it
  // only updates its scroll velocity from pointer events on the canvas,
  // which stop firing the moment the cursor crosses out. The math here
  // intentionally mirrors `MouseEdges.update` so the visible scroll speed
  // matches what the user feels when the cursor is parked inside the edge
  // zone of the canvas.
  // `allowScroll` enables the edge auto-scroll (used by selection / move
  // gestures). A heading resize passes `false`: on-canvas it never edge-scrolls
  // (MouseEdges isn't enabled for it), so off-canvas it should only keep
  // tracking the cursor, not start scrolling the sheet in the perpendicular
  // axis when the cursor strays onto the toolbar.
  scrollViewportFromOffCanvasCursor = (clientX: number, clientY: number, allowScroll = true): boolean => {
    const canvas = this.pixiApp.canvas;
    if (!canvas) return false;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    if (allowScroll) {
      let dx = 0;
      let dy = 0;
      if (x < MOUSE_EDGES_DISTANCE) dx = -1;
      else if (x > rect.width - MOUSE_EDGES_DISTANCE) dx = 1;
      if (y < MOUSE_EDGES_DISTANCE) dy = -1;
      else if (y > rect.height - MOUSE_EDGES_DISTANCE) dy = 1;

      // Zero out any direction that would push past a clamp boundary. Without
      // this guard, once the viewport reaches the start of the sheet (left
      // or top) every RAF frame would still overshoot the clamp, then
      // `handleMoved` would snap us back via `position.set` and flip
      // `dirty = true`, causing the main update loop to do a full render of
      // an unchanged scene every frame. That render storm is what makes the
      // page freeze when the user drags back to the top-left corner.
      const { contentBounds, clampVisibleToContentEdges, leftClamp, topClamp } = getViewportClampConfig(
        content.headings.headingSize
      );
      if (dx < 0 && this.x >= leftClamp) dx = 0;
      if (dy < 0 && this.y >= topClamp) dy = 0;
      if (clampVisibleToContentEdges && contentBounds) {
        const visibleBounds = this.getVisibleBounds();
        if (dx > 0 && visibleBounds.right >= contentBounds.right) dx = 0;
        if (dy > 0 && visibleBounds.bottom >= contentBounds.bottom) dy = 0;
      }

      if (dx !== 0 || dy !== 0) {
        const speed = MOUSE_EDGES_SPEED / this.scale.x;
        const perFrameDelta = speed * speed * (60 / 1000);
        const center = this.center;
        center.x += dx * perFrameDelta;
        center.y += dy * perFrameDelta;
        this.moveCenter(center);

        // Mirrors `MouseEdges.update`: announces the auto-scroll move so
        // `handleMoved` can clamp at content edges.
        this.emit('moved', { viewport: this, type: 'mouse-edges' });
      }
    }

    // Always synthesize a clamped pointer move so the active gesture (selection,
    // move, or heading resize) keeps tracking the cursor while it is off the
    // canvas - even on frames where no auto-scroll happens (e.g. the cursor is
    // parked over the toolbar at the top clamp, or `allowScroll` is false for a
    // resize). Without this the gesture freezes the moment the cursor leaves
    // the canvas DOM rect, since PixiJS stops delivering pointer events there.
    //
    // For off-canvas auto-scroll, the renderer's last on-canvas pointer
    // position is fixed at wherever the cursor crossed off the canvas. As
    // the viewport scrolls, the world position derived from that fixed
    // screen point drifts outside the visible content area, which would
    // draw the selection / move-target outline off the visible canvas (or
    // into the row/column heading strip). Project the pointer onto a
    // point just inside the visible content area, in the same direction
    // the cursor actually went off, so the outline tracks the visible
    // edge instead. (For in-canvas MouseEdges scrolling the cursor is
    // already on the canvas, so `handleMouseEdgeMove` keeps using the
    // unclamped pointer there.)
    const headingSize = content.headings.headingSize;
    const minX = headingSize.width + 1;
    const minY = headingSize.height + 1;
    const maxX = rect.width - 1;
    const maxY = rect.height - 1;
    const projectedX = Math.min(Math.max(x, minX), maxX);
    const projectedY = Math.min(Math.max(y, minY), maxY);
    const pointer = this.pixiApp.renderer.events.pointer;
    const originalGlobalX = pointer.global.x;
    const originalGlobalY = pointer.global.y;
    pointer.global.x = projectedX;
    pointer.global.y = projectedY;
    this.pixiApp.pointer.pointerMove(pointer);
    pointer.global.x = originalGlobalX;
    pointer.global.y = originalGlobalY;
    return true;
  };

  // Pause/resume drag plugin (used for mobile selection expansion)
  pauseDrag = () => {
    const drag = this.plugins.get('drag') as Drag | null;
    if (drag) {
      drag.pause();
    }
  };

  resumeDrag = () => {
    const drag = this.plugins.get('drag') as Drag | null;
    if (drag) {
      drag.resume();
    }
  };

  sendRenderViewport() {
    const bounds = this.getClampedVisibleBounds();
    const scale = this.scale.x;
    const { contentBounds, clampVisibleToContentEdges } = getViewportClampConfig(content.headings.headingSize);

    // Only send clamp bounds if:
    // 1. Clamping is enabled with valid bounds
    // 2. If embedClampToRange is set, the range must be actually resolvable (not fallback)
    let clampBounds: Rectangle | undefined;
    if (clampVisibleToContentEdges && contentBounds) {
      // Check if this is a valid clamp range (not fallback to full content)
      if (embedClampToRange && sheets.sheets.length > 0 && !sheets.sheet.hasClampRange()) {
        // Range isn't resolvable yet - don't send clamp bounds, let main thread hide content
        clampBounds = undefined;
      } else {
        clampBounds = contentBounds;
      }
    }

    renderWebWorker.updateViewport(sheets.current, bounds, scale, clampBounds);
  }

  getClampedVisibleBounds(): Rectangle {
    const visibleBounds = this.getVisibleBounds();
    const { contentBounds, clampVisibleToContentEdges } = getViewportClampConfig(content.headings.headingSize);

    if (!clampVisibleToContentEdges || !contentBounds) {
      return visibleBounds;
    }

    const left = Math.max(visibleBounds.left, contentBounds.left);
    const top = Math.max(visibleBounds.top, contentBounds.top);
    const right = Math.min(visibleBounds.right, contentBounds.right);
    const bottom = Math.min(visibleBounds.bottom, contentBounds.bottom);

    if (right <= left || bottom <= top) {
      return new Rectangle(contentBounds.x, contentBounds.y, 0, 0);
    }

    return new Rectangle(left, top, right - left, bottom - top);
  }

  /** World top-left after content clamp; same target as {@link startSnap} without animating. */
  private computeContentClampSnapTargetWorldCorner(): { x: number; y: number; needsSnap: boolean } {
    let x: number;
    let y: number;
    let needsSnap = false;

    const { contentBounds, clampVisibleToContentEdges, leftClamp, topClamp } = getViewportClampConfig(
      content.headings.headingSize
    );

    // For embed clamp mode, also snap when content is clipped under heading (this.x < leftClamp)
    if (this.x > leftClamp || (embedClampToRange && this.x < leftClamp)) {
      x = -leftClamp / this.scaled;
      needsSnap = true;
    } else {
      x = -this.x / this.scaled;
    }

    if (this.y > topClamp || (embedClampToRange && this.y < topClamp)) {
      y = -topClamp / this.scaled;
      needsSnap = true;
    } else {
      y = -this.y / this.scaled;
    }

    if (clampVisibleToContentEdges && contentBounds) {
      const visibleBounds = this.getVisibleBounds();
      // minX/minY should position content left/top edge at the heading edge, not at screen edge
      const minX = embedClampToRange ? -leftClamp / this.scaled : 0;
      const minY = embedClampToRange ? -topClamp / this.scaled : 0;

      if (visibleBounds.right > contentBounds.right) {
        const targetX = contentBounds.right - visibleBounds.width + 1 / this.scaled;
        x = Math.max(minX, targetX);
        needsSnap = true;
      }

      if (visibleBounds.bottom > contentBounds.bottom) {
        const targetY = contentBounds.bottom - visibleBounds.height + 1 / this.scaled;
        y = Math.max(minY, targetY);
        needsSnap = true;
      }
    }

    return { x, y, needsSnap };
  }

  private startSnap = () => {
    const { x, y, needsSnap } = this.computeContentClampSnapTargetWorldCorner();

    if (needsSnap) {
      this.snap(x, y, {
        topLeft: true,
        time: SNAPPING_TIME,
        ease: 'easeOutSine',
        removeOnComplete: true,
        removeOnInterrupt: true,
        interrupt: false,
      });
      this.snapState = 'snapping';
    } else {
      this.snapState = undefined;
    }
  };

  private handleMoved = (event: { viewport: Viewport; type: string }) => {
    if (event.type === 'mouse-edges') {
      if (this.pixiApp.pointer.pointerHeading.movingColRows) return;

      let needsClamp = false;
      let nextX = this.x;
      let nextY = this.y;

      const { contentBounds, clampVisibleToContentEdges, leftClamp, topClamp } = getViewportClampConfig(
        content.headings.headingSize
      );

      // Check left boundary (also prevent clipping under heading in embed clamp mode)
      if (this.x > leftClamp || (embedClampToRange && this.x < leftClamp)) {
        nextX = leftClamp;
        needsClamp = true;
      }

      // Check top boundary (also prevent clipping under heading in embed clamp mode)
      if (this.y > topClamp || (embedClampToRange && this.y < topClamp)) {
        nextY = topClamp;
        needsClamp = true;
      }

      // Check right/bottom boundaries when content clamping is enabled
      if (clampVisibleToContentEdges && contentBounds) {
        const visibleBounds = this.getVisibleBounds();
        // minX/minY should position content left/top edge at the heading edge, not at screen edge
        const minX = embedClampToRange ? -leftClamp / this.scaled : 0;
        const minY = embedClampToRange ? -topClamp / this.scaled : 0;

        // Check right boundary
        if (visibleBounds.right > contentBounds.right) {
          // Calculate where left edge should be to align right edges
          const targetX = contentBounds.right - visibleBounds.width;
          // If content is smaller than viewport, use left edge of range; otherwise align right edges
          const clampX = Math.max(targetX, minX);
          nextX = Math.min(nextX, -clampX * this.scaled - BORDER_VISIBILITY_OFFSET);
          needsClamp = true;
        }

        // Check bottom boundary
        if (visibleBounds.bottom > contentBounds.bottom) {
          // Calculate where top edge should be to align bottom edges
          const targetY = contentBounds.bottom - visibleBounds.height;
          // If content is smaller than viewport, use top edge of range; otherwise align bottom edges
          const clampY = Math.max(targetY, minY);
          nextY = Math.min(nextY, -clampY * this.scaled - BORDER_VISIBILITY_OFFSET);
          needsClamp = true;
        }
      }

      if (needsClamp) {
        this.disableMouseEdges();
        this.position.set(nextX, nextY);
        this.dirty = true;
      }
    }
  };

  // Returns true if the viewport has changed
  updateViewport = (): boolean => {
    let dirty = false;
    if (this.lastViewportScale !== this.scale.x) {
      this.lastViewportScale = this.scale.x;
      dirty = true;

      // this is used to trigger changes to ZoomDropdown
      events.emit('zoom', this.scale.x);
    }
    if (this.lastViewportPosition.x !== this.x || this.lastViewportPosition.y !== this.y) {
      this.lastViewportPosition.x = this.x;
      this.lastViewportPosition.y = this.y;
      dirty = true;
    }
    if (this.lastScreenWidth !== this.screenWidth || this.lastScreenHeight !== this.screenHeight) {
      this.lastScreenWidth = this.screenWidth;
      this.lastScreenHeight = this.screenHeight;
      dirty = true;
    }
    if (this.lastSheetId !== sheets.current) {
      this.lastSheetId = sheets.current;
      dirty = true;
    }
    if (dirty) {
      this.pixiApp.viewportChanged();
      this.sendRenderViewport();

      // signals to react that the viewport has changed (so it can update any
      // related positioning)
      events.emit('viewportChangedReady');

      // Clear both timeout and state when interrupting a waiting snap
      this.snapTimeout = undefined;
      this.snapState = undefined;
    } else if (!this.waitForZoomEnd) {
      if (!this.snapState) {
        const { leftClamp, topClamp } = getViewportClampConfig(content.headings.headingSize);

        // Only snap when the top-left corner is misaligned (e.g. after zooming
        // out). Right/bottom overflow during normal scrolling should not trigger
        // a snap — the zoom-end handler corrects all boundaries after zoom.
        const needsSnap = this.x > leftClamp || this.y > topClamp;

        if (needsSnap) {
          if (this.pixiApp.momentumDetector.hasMomentumScroll()) {
            if (!this.plugins.get('drag')?.active) {
              this.startSnap();
            }
          } else {
            this.snapTimeout = Date.now();
            this.snapState = 'waiting';
          }
        }
      } else if (this.snapState === 'waiting' && this.snapTimeout) {
        if (Date.now() - this.snapTimeout > WAIT_TO_SNAP_TIME) {
          // Check for trackpad pinch using pointer type
          const isPinching = window.TouchEvent && navigator.maxTouchPoints > 0 && (window as any).touches?.length > 1;
          if (!this.plugins.get('drag')?.active && !isPinching) {
            this.startSnap();
          }
        }
      }
    }
    return dirty;
  };

  private handleWaitForZoomEnd = () => {
    this.waitForZoomEnd = true;
  };

  private handleZoomEnd = () => {
    this.waitForZoomEnd = false;
    this.startSnap();
  };

  private handleSnapEnd = () => {
    this.snapState = undefined;
    this.snapTimeout = undefined;
  };

  private handleMouseEdgeMove = (event: { viewport: Viewport; type: string }) => {
    if (event.type === 'mouse-edges') {
      pixiApp.pointer.pointerMove(this.pixiApp.renderer.events.pointer);
    }
  };

  changeX(x: number) {
    if (this.x === x) return;
    this.x = x;
    this.dirty = true;
  }

  changeY(y: number) {
    if (this.y === y) return;
    this.y = y;
    this.dirty = true;
  }
}
